import React, { useEffect } from 'react';
import { Box, Typography, Alert, CircularProgress, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useMySubjects } from '../../survey/hooks/useLecturerSubjects';
import { useContextStore } from '../../../stores/contextStore';
import { useAuthStore } from '../../authentication/store/authStore';

export const LecturerManageWrapper: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { activeTenantId, activeSubjectId, activeDomainId } = useContextStore();

  // Get subjects accessible to the current user based on their roles
  const {
    data: subjectsData,
    isLoading,
    error,
  } = useMySubjects(user?.id || '', activeTenantId || '', activeDomainId || undefined);
  const subjects = subjectsData?.items;

  // Find the current subject based on the active context
  const currentSubject =
    subjects?.find((subject) => subject.subjectId === activeSubjectId) || subjects?.[0];

  useEffect(() => {
    // Once we have the subject data, navigate to the proper subject management URL
    if (currentSubject) {
      const { tenantId, domainId, subjectId } = currentSubject;
      navigate(
        `/app/manage/universities/${tenantId}/institutes/${domainId}/subjects/${subjectId}/users`,
        {
          replace: true,
        }
      );
    }
  }, [currentSubject, navigate]);

  // Handle loading state
  if (isLoading) {
    return (
      <Container maxWidth={false} disableGutters>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading your management page...</Typography>
        </Box>
      </Container>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Container maxWidth={false} disableGutters>
        <Alert severity="error">
          Failed to load your management context. Please try refreshing the page.
        </Alert>
      </Container>
    );
  }

  // Handle no subjects state
  if (!currentSubject) {
    return (
      <Container maxWidth={false} disableGutters>
        <Alert severity="warning">
          <Typography variant="h6">No Subject Assigned</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            You are not currently assigned to any subjects. Please contact your administrator to get
            assigned to a subject.
          </Typography>
        </Alert>
      </Container>
    );
  }

  // During navigation, show loading state
  return (
    <Container maxWidth={false} disableGutters>
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Redirecting to your management page...</Typography>
      </Box>
    </Container>
  );
};
